'use client';

import { useState } from 'react';
import type { OlingoModel } from '@/lib/content/ai-models';
import { CheckTick, ChevronDown } from '@/app/components/common/Icons';

type Props = {
  models: OlingoModel[];
  eyebrow: string;
  title: string;
  intro: string;
};

function ModelPanel({ model }: { model: OlingoModel }) {
  return (
    <div className="flex flex-col gap-8">
      <p className="font-mono text-eyebrow tracking-widest uppercase text-font-quaternary">
        {model.tagline}
      </p>
      <div className="flex flex-col gap-4">
        <h3 className="text-[length:var(--text-feature-title)] font-normal tracking-[-0.02em] leading-tight text-font-primary">
          {model.name}
        </h3>
        <p className="text-subheadline text-font-primary/70 font-light leading-relaxed">
          {model.description}
        </p>
      </div>
      <div className="grid grid-cols-2 gap-x-8 gap-y-4 pt-4 border-t border-white/25">
        {model.features.map((f) => (
          <div key={f} className="flex items-start gap-3">
            <CheckTick className="shrink-0 mt-[5px] size-5 text-font-quaternary" />
            <span className="text-nav text-font-primary/70 leading-relaxed">{f}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function OlingoTabs({ models, eyebrow, title, intro }: Props) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section className="bg-background-secondary px-[10%] py-24" data-header-dark>
      <div className="max-w-6xl">

        {/* ── Desktop: Horizontal Tabs ── */}
        <div className="hidden lg:flex gap-20 items-start">

          {/* Left column — text */}
          <div className="flex flex-col gap-5 w-[35%] shrink-0">
            <p className="font-mono text-eyebrow tracking-[var(--hero-label-tracking)] uppercase text-font-quaternary">
              {eyebrow}
            </p>
            <h2 className="text-[length:var(--text-section-title)] font-normal tracking-[-0.02em] leading-[1.1] text-font-primary">
              {title}
            </h2>
            <p className="text-subheadline text-font-primary/60 font-light leading-relaxed">
              {intro}
            </p>
          </div>

          {/* Right column — tabs + panel */}
          <div className="flex-1 min-w-0 flex flex-col gap-10">
            <nav className="flex items-end gap-0 border-b border-white/10" aria-label="Model tabs">
              {models.map((model, i) => (
                <button
                  key={model.slug}
                  onClick={() => setActiveIndex(i)}
                  aria-pressed={i === activeIndex}
                  className={[
                    'flex items-center gap-2 pb-4 pr-8 text-left border-b-[2px] -mb-px transition-colors duration-200',
                    i === activeIndex
                      ? 'border-white text-font-primary'
                      : 'border-transparent text-font-primary/30 hover:text-font-primary/55',
                  ].join(' ')}
                >
                  <span className="font-mono text-eyebrow tabular-nums text-font-quaternary/70">
                    {String(i + 1).padStart(2, '0')}.
                  </span>
                  <span className="text-subheadline font-medium">{model.name}</span>
                </button>
              ))}
            </nav>

            <div className="grid">
              {models.map((model, i) => (
                <div
                  key={model.slug}
                  className={`[grid-area:1/1] transition-opacity duration-300 ${
                    i === activeIndex ? 'opacity-100' : 'opacity-0 pointer-events-none select-none'
                  }`}
                >
                  <ModelPanel model={model} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Mobile: Accordion ── */}
        <div className="lg:hidden flex flex-col gap-10">
          <div className="flex flex-col gap-5">
            <p className="font-mono text-eyebrow tracking-[var(--hero-label-tracking)] uppercase text-font-quaternary">
              {eyebrow}
            </p>
            <h2 className="text-[length:var(--text-section-title)] font-normal tracking-[-0.02em] leading-[1.1] text-font-primary">
              {title}
            </h2>
            <p className="text-subheadline text-font-primary/60 font-light leading-relaxed">
              {intro}
            </p>
          </div>

          <div className="flex flex-col divide-y divide-white/10 border-t border-white/10 border-b border-white/10">
            {models.map((model, i) => {
              const isOpen = openIndex === i;
              return (
                <div key={model.slug}>
                  <button
                    onClick={() => setOpenIndex(isOpen ? null : i)}
                    aria-expanded={isOpen}
                    className="w-full flex items-center justify-between py-5 text-left gap-4"
                  >
                    <div className="flex flex-col gap-1">
                      <span className="text-subheadline font-medium text-font-primary">
                        {model.name}
                      </span>
                      <span className="font-mono text-eyebrow uppercase tracking-widest text-font-quaternary/65">
                        {model.tagline}
                      </span>
                    </div>
                    <ChevronDown className={`size-5 shrink-0 text-font-primary/40 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} />
                  </button>

                  <div
                    className="grid transition-[grid-template-rows] duration-300 ease-out"
                    style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
                  >
                    <div className="overflow-hidden">
                      <div className="pb-7 flex flex-col gap-5">
                        <p className="text-subheadline text-font-primary/60 font-light leading-relaxed">
                          {model.description}
                        </p>
                        <div className="flex flex-col gap-3">
                          {model.features.map((f) => (
                            <div key={f} className="flex items-start gap-3">
                              <CheckTick className="shrink-0 mt-[5px] size-5 text-font-quaternary" />
                              <span className="text-nav text-font-primary/55 leading-relaxed">{f}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </section>
  );
}
